import { format, isBefore, startOfDay } from "date-fns";
import { parseLocalDateOnly } from "@/lib/dateUtils";

export type TurnoverChipState =
  | "Upcoming"
  | "Open"
  | "Same day"
  | "Overdue"
  | "In progress"
  | "Done"
  | "Guest left"
  | "Waiting on guest"
  | "Updated"
  | "Test clean";

export type ReservationDateRow = {
  id: string;
  start_date: string;
  end_date: string;
  guest_name?: string | null;
  guest_count?: number | null;
  guest_photo_url?: string | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  check_out_previous?: string | null;
  check_out_slipped_at?: string | null;
  check_in_previous?: string | null;
  check_in_slipped_at?: string | null;
  early_checkout_detected?: boolean | null;
  checkout_unconfirmed_at?: string | null;
  reservation_type?: string | null;
  is_test?: boolean | null;
  summary?: string | null;
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayYmd(now = new Date()): string {
  return ymd(now);
}

/** Next reservation that starts on/after checkout (excluding the departing stay). */
export function findNextCheckIn(
  checkoutDate: string,
  reservations: ReservationDateRow[],
  departingReservationId?: string | null,
): ReservationDateRow | null {
  const checkout = checkoutDate.slice(0, 10);
  const candidates = reservations
    .filter((r) => {
      if (departingReservationId && r.id === departingReservationId) return false;
      if (r.reservation_type === "blocked") return false;
      return r.start_date.slice(0, 10) >= checkout;
    })
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  return candidates[0] ?? null;
}

export function formatWindowLabel(checkoutDate: string, nextCheckInDate: string | null): string {
  const start = parseLocalDateOnly(checkoutDate);
  if (!nextCheckInDate) {
    return `After ${format(start, "MMM d")}`;
  }
  const end = parseLocalDateOnly(nextCheckInDate);
  if (ymd(start) === ymd(end)) {
    return `Same day · ${format(start, "MMM d")}`;
  }
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `Turnover · ${format(start, "MMM d")}–${format(end, "d")}`;
  }
  return `Turnover · ${format(start, "MMM d")}–${format(end, "MMM d")}`;
}

/** "11:00" / "16:00:00" / "4:00 PM" → "11:00 AM" / "4:00 PM". Minutes always shown (Slice 12b). */
export function formatClock12(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const s = raw.trim();
  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    const h = Number(ampm[1]);
    const min = ampm[2];
    const ap = ampm[3].toUpperCase();
    return `${h}:${min} ${ap}`;
  }
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  let h = Number(m[1]);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ap}`;
}

/** First name possessive: "John Smith" → "John's". Missing → "Guest's". */
export function groupPossessiveName(guestName?: string | null): string {
  const first = (guestName || "").trim().split(/\s+/)[0];
  if (!first) return "Guest's";
  if (/s$/i.test(first)) return `${first}'`;
  return `${first}'s`;
}

export function formatShortMonthDay(dateYmd: string): string {
  return format(parseLocalDateOnly(dateYmd.slice(0, 10)), "MMM d");
}

/** Owner Schedule / Assign: "John's group of 6 checks out Sep 22 at 1:00 PM" */
export function formatGroupSentence(opts: {
  guestName?: string | null;
  guestCount?: number | null;
  verb: "checks out" | "checks in";
  dateYmd: string;
  clock?: string | null;
}): string {
  const who = groupPossessiveName(opts.guestName);
  const count = opts.guestCount != null && opts.guestCount > 0 ? opts.guestCount : null;
  const party = count != null ? ` group of ${count}` : " group";
  const date = formatShortMonthDay(opts.dateYmd);
  const time = formatClock12(opts.clock);
  return time
    ? `${who}${party} ${opts.verb} ${date} at ${time}`
    : `${who}${party} ${opts.verb} ${date}`;
}

export type GroupSentenceParts = { lead: string; time: string };

/** Split so the clock can render bold: lead includes trailing "at ". */
export function splitGroupSentence(sentence: string): GroupSentenceParts {
  const i = sentence.lastIndexOf(" at ");
  if (i === -1) return { lead: sentence, time: "" };
  return { lead: sentence.slice(0, i + 4), time: sentence.slice(i + 4) };
}

/** Cleaner Jobs / Job Start — Slice 12c plain clocks (never bare “Out” / “In”). */
export type CleanerClockTone = "default" | "teal" | "amber" | "muted";

export type CleanerClockCopy = {
  /** Jobs list primary line */
  primary: string | null;
  /** Jobs list muted second line */
  secondary: string | null;
  /** Job Start card primary (may split clocks across two lines) */
  detailPrimary: string | null;
  /** Job Start card muted second line */
  detailSecondary: string | null;
  tone: CleanerClockTone;
};

function nextGuestLine(checkInTime?: string | null): string | null {
  const inn = formatClock12(checkInTime);
  return inn ? `Next guest checks in ${inn}` : null;
}

function checkoutClause(checkoutTime?: string | null, opts?: { by?: boolean; updated?: boolean; was?: boolean }): string | null {
  const out = formatClock12(checkoutTime);
  if (!out) return null;
  if (opts?.was) return `Checkout was ${out}`;
  if (opts?.updated) return `Checkout ${out} (updated)`;
  if (opts?.by) return `Checkout by ${out}`;
  return `Checkout ${out}`;
}

/**
 * Slice 12c copy matrix for cleaner surfaces.
 * Never “Out” or “In” alone. “Guest left” is a status, never paired with a bare time.
 */
export function formatCleanerClockCopy(opts: {
  checkoutTime?: string | null;
  checkInTime?: string | null;
  guestLeft?: boolean | null;
  waitingOnGuest?: boolean | null;
  checkoutUpdated?: boolean | null;
  isTest?: boolean | null;
}): CleanerClockCopy {
  if (opts.isTest) {
    const line = "Test clean — no guest clocks";
    return {
      primary: line,
      secondary: null,
      detailPrimary: line,
      detailSecondary: null,
      tone: "muted",
    };
  }

  const next = nextGuestLine(opts.checkInTime);
  const out = formatClock12(opts.checkoutTime);

  if (opts.guestLeft) {
    const primary = "Guest left — you can start";
    return {
      primary,
      secondary: next,
      detailPrimary: primary,
      detailSecondary: next,
      tone: "teal",
    };
  }

  if (opts.waitingOnGuest) {
    const primary = "Still waiting on guest to leave";
    const was = checkoutClause(opts.checkoutTime, { was: true });
    const secondary = [was, next].filter(Boolean).join(" · ") || null;
    return {
      primary,
      secondary,
      detailPrimary: primary,
      detailSecondary: secondary,
      tone: "amber",
    };
  }

  if (opts.checkoutUpdated && out) {
    const primary = checkoutClause(opts.checkoutTime, { updated: true })!;
    return {
      primary,
      secondary: next,
      detailPrimary: primary,
      detailSecondary: next,
      tone: "default",
    };
  }

  if (out && next) {
    const combined = `Checkout ${out} · ${next}`;
    return {
      primary: combined,
      secondary: null,
      detailPrimary: `Checkout ${out}`,
      detailSecondary: next,
      tone: "default",
    };
  }

  if (out) {
    // Open-ended window (no next guest) — “by” makes the deadline clear.
    const primary = checkoutClause(opts.checkoutTime, { by: true })!;
    return {
      primary,
      secondary: null,
      detailPrimary: primary,
      detailSecondary: null,
      tone: "default",
    };
  }

  if (next) {
    return {
      primary: next,
      secondary: null,
      detailPrimary: next,
      detailSecondary: null,
      tone: "default",
    };
  }

  return {
    primary: null,
    secondary: null,
    detailPrimary: null,
    detailSecondary: null,
    tone: "default",
  };
}

/** @deprecated Prefer formatCleanerClockCopy — returns Jobs list primary only. */
export function formatCleanerClock(opts: {
  checkoutTime?: string | null;
  checkInTime?: string | null;
  guestLeft?: boolean | null;
  waitingOnGuest?: boolean | null;
  checkoutUpdated?: boolean | null;
  isTest?: boolean | null;
}): string | null {
  return formatCleanerClockCopy(opts).primary;
}

export function minutesBetweenClocks(
  checkoutTime?: string | null,
  checkInTime?: string | null,
): number | null {
  const parse = (t: string | null | undefined): number | null => {
    if (!t?.trim()) return null;
    const ampm = t.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (ampm) {
      let h = Number(ampm[1]);
      const min = Number(ampm[2]);
      const ap = ampm[3].toUpperCase();
      if (ap === "PM" && h !== 12) h += 12;
      if (ap === "AM" && h === 12) h = 0;
      return h * 60 + min;
    }
    const m = t.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const a = parse(checkoutTime);
  const b = parse(checkInTime);
  if (a == null || b == null) return null;
  const diff = b - a;
  return diff > 0 ? diff : null;
}

/** @deprecated use formatCleanerClock */
export function formatSameDaySecondary(
  checkoutTime?: string | null,
  checkInTime?: string | null,
  opts?: { guestLeft?: boolean | null },
): string | null {
  return formatCleanerClock({
    checkoutTime,
    checkInTime,
    guestLeft: opts?.guestLeft,
  });
}

export type TurnoverWindow = {
  label: string;
  chip: TurnoverChipState;
  /** Cleaner clock line (times only). Null on Done. */
  sub: string | null;
  cleanerClock: string | null;
  cleanerClockSecondary: string | null;
  cleanerClockDetailPrimary: string | null;
  cleanerClockDetailSecondary: string | null;
  cleanerClockTone: CleanerClockTone;
  outSentence: string | null;
  inSentence: string | null;
  /** Quiet owner/cleaner status: Guest left / Waiting / Updated. */
  statusNote: string | null;
  outGuestName: string | null;
  inGuestName: string | null;
  outGuestPhotoUrl: string | null;
  inGuestPhotoUrl: string | null;
  checkoutDate: string;
  nextCheckInDate: string | null;
  sameDay: boolean;
  openEnded: boolean;
  guestLeft: boolean;
  waitingOnGuest: boolean;
  checkoutUpdated: boolean;
  checkOutPrevious: string | null;
  departingCheckOutTime: string | null;
  arrivingCheckInTime: string | null;
  windowMinutes: number | null;
  tightWindow: boolean;
};

export function resolveTurnoverWindow(opts: {
  checkoutDate: string;
  reservations: ReservationDateRow[];
  departingReservationId?: string | null;
  overrideCheckOutTime?: string | null;
  taskStatus?: string | null;
  isTest?: boolean | null;
  now?: Date;
}): TurnoverWindow {
  const checkoutDate = opts.checkoutDate.slice(0, 10);
  const departing =
    (opts.departingReservationId
      ? opts.reservations.find((r) => r.id === opts.departingReservationId)
      : null) ||
    opts.reservations.find(
      (r) => r.reservation_type !== "blocked" && r.end_date?.slice(0, 10) === checkoutDate,
    ) ||
    null;
  const next = findNextCheckIn(checkoutDate, opts.reservations, opts.departingReservationId);
  const nextCheckInDate = next?.start_date?.slice(0, 10) ?? null;
  const sameDay = Boolean(nextCheckInDate && nextCheckInDate === checkoutDate);
  const openEnded = !nextCheckInDate;
  const label = formatWindowLabel(checkoutDate, nextCheckInDate);
  const departingCheckOutTime =
    (opts.overrideCheckOutTime || "").trim() || departing?.check_out_time || null;
  const arrivingCheckInTime = next?.check_in_time || null;
  const guestLeft = Boolean(departing?.early_checkout_detected);
  const waitingOnGuestRaw = !guestLeft && Boolean(departing?.checkout_unconfirmed_at);
  const today = todayYmd(opts.now);
  // Waiting only applies on/before checkout day — past days must not stay stuck.
  const waitingOnGuest = waitingOnGuestRaw && today <= checkoutDate;
  const checkoutUpdated =
    !guestLeft &&
    !waitingOnGuestRaw &&
    Boolean(departing?.check_out_slipped_at) &&
    Boolean(departing?.check_out_previous);
  const checkinUpdated =
    Boolean(next?.check_in_slipped_at) && Boolean(next?.check_in_previous);
  const windowMinutes = minutesBetweenClocks(departingCheckOutTime, arrivingCheckInTime);
  const tightWindow = sameDay && windowMinutes != null && windowMinutes < 5 * 60;
  const clockCopy = formatCleanerClockCopy({
    checkoutTime: departingCheckOutTime,
    checkInTime: arrivingCheckInTime,
    guestLeft,
    waitingOnGuest,
    checkoutUpdated,
    isTest: opts.isTest,
  });
  const cleanerClock = clockCopy.primary;
  const outSentence = departing
    ? formatGroupSentence({
        guestName: departing.guest_name,
        guestCount: departing.guest_count,
        verb: "checks out",
        dateYmd: checkoutDate,
        clock: departingCheckOutTime,
      })
    : departingCheckOutTime
      ? formatGroupSentence({
          verb: "checks out",
          dateYmd: checkoutDate,
          clock: departingCheckOutTime,
        })
      : null;
  const inSentence = nextCheckInDate
    ? formatGroupSentence({
        guestName: next?.guest_name,
        guestCount: next?.guest_count,
        verb: "checks in",
        dateYmd: nextCheckInDate,
        clock: arrivingCheckInTime,
      })
    : null;

  // Owner secondary status lines (Slice 26) — do not replace check-in sentence.
  const statusNote = guestLeft
    ? "Guest left"
    : waitingOnGuest
      ? `Waiting on guest · past ${formatClock12(departingCheckOutTime) || "checkout"}`
      : checkoutUpdated && departing?.check_out_previous
        ? `Updated · was ${formatClock12(departing.check_out_previous)}`
        : checkinUpdated && next?.check_in_previous
          ? `Early check-in · was ${formatClock12(next.check_in_previous)}`
          : null;

  const empty: Omit<TurnoverWindow, "chip" | "sub"> = {
    label,
    cleanerClock,
    cleanerClockSecondary: clockCopy.secondary,
    cleanerClockDetailPrimary: clockCopy.detailPrimary,
    cleanerClockDetailSecondary: clockCopy.detailSecondary,
    cleanerClockTone: clockCopy.tone,
    outSentence,
    inSentence,
    statusNote,
    outGuestName: departing?.guest_name?.trim() || null,
    inGuestName: next?.guest_name?.trim() || null,
    outGuestPhotoUrl: departing?.guest_photo_url?.trim() || null,
    inGuestPhotoUrl: next?.guest_photo_url?.trim() || null,
    checkoutDate,
    nextCheckInDate,
    sameDay,
    openEnded,
    guestLeft,
    waitingOnGuest,
    checkoutUpdated,
    checkOutPrevious: departing?.check_out_previous?.trim() || null,
    departingCheckOutTime,
    arrivingCheckInTime,
    windowMinutes,
    tightWindow,
  };

  const status = opts.taskStatus || "scheduled";
  if (status === "completed") {
    return { ...empty, chip: "Done", sub: null };
  }
  if (status === "in_progress") {
    return { ...empty, chip: "In progress", sub: cleanerClock };
  }

  let chip: TurnoverChipState;

  if (opts.isTest) {
    chip = "Test clean";
  } else if (guestLeft) {
    chip = "Guest left";
  } else if (waitingOnGuest) {
    chip = "Waiting on guest";
  } else if (today < checkoutDate) {
    chip = "Upcoming";
  } else if (nextCheckInDate && today > nextCheckInDate) {
    chip = "Overdue";
  } else if (sameDay) {
    chip = checkoutUpdated ? "Updated" : "Same day";
  } else {
    chip = "Open";
  }

  return { ...empty, chip, sub: cleanerClock };
}

export function withTurnoverClock<T extends {
  scheduled_date: string;
  status?: string | null;
  reservation_id?: string | null;
  override_check_out_time?: string | null;
  is_test?: boolean | null;
}>(
  task: T,
  reservations: ReservationDateRow[],
): T & {
  arriving_check_in_time: string | null;
  departing_check_out_time: string | null;
  guest_left: boolean;
  waiting_on_guest: boolean;
  checkout_updated: boolean;
  status_note: string | null;
  same_day_turnover: boolean;
  window_label: string;
  window_times_label: string | null;
  window_times_secondary: string | null;
  window_times_detail_primary: string | null;
  window_times_detail_secondary: string | null;
  window_times_tone: CleanerClockTone;
  window_chip: TurnoverChipState;
  tight_window: boolean;
} {
  const w = resolveTurnoverWindow({
    checkoutDate: task.scheduled_date,
    reservations,
    departingReservationId: task.reservation_id,
    overrideCheckOutTime: task.override_check_out_time,
    taskStatus: task.status,
    isTest: task.is_test,
  });
  return {
    ...task,
    arriving_check_in_time: w.arrivingCheckInTime,
    departing_check_out_time: w.departingCheckOutTime,
    guest_left: w.guestLeft,
    waiting_on_guest: w.waitingOnGuest,
    checkout_updated: w.checkoutUpdated,
    status_note: w.statusNote,
    same_day_turnover: w.sameDay,
    window_label: w.label,
    window_times_label: w.cleanerClockDetailPrimary ?? w.cleanerClock,
    window_times_secondary: w.cleanerClockDetailSecondary,
    window_times_detail_primary: w.cleanerClockDetailPrimary,
    window_times_detail_secondary: w.cleanerClockDetailSecondary,
    window_times_tone: w.cleanerClockTone,
    window_chip: w.chip,
    tight_window: w.tightWindow,
  };
}

/**
 * Cleaners may start at local midnight on the first turnover day (checkout day).
 * Example: Turnover · Sep 22–23 → Start from Sep 22 at 12:00 AM local, not before.
 * Still allowed after that day (multi-day window / overdue).
 */
export function canStartTurnoverClean(checkoutDate: string, now = new Date()): boolean {
  const dateOnly = (checkoutDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return false;
  const opensAt = startOfDay(parseLocalDateOnly(dateOnly));
  return !isBefore(now, opensAt);
}

/** Local midnight when Start unlocks for this turnover. */
export function turnoverOpensAt(checkoutDate: string): Date | null {
  const dateOnly = (checkoutDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  return startOfDay(parseLocalDateOnly(dateOnly));
}

/** e.g. "Opens Sep 22 at 12:00 AM" — for Upcoming / Not open yet. */
export function formatTurnoverOpensHint(checkoutDate: string, now = new Date()): string | null {
  const opensAt = turnoverOpensAt(checkoutDate);
  if (!opensAt) return null;
  if (!isBefore(now, opensAt)) return null;
  return `Opens ${format(opensAt, "MMM d")} at 12:00 AM`;
}

export function chipClassName(chip: TurnoverChipState): string {
  switch (chip) {
    case "Open":
      return "bg-[#eaf6f4] text-[#15776d]";
    case "Same day":
      return "bg-[#24a89a] text-white";
    case "Guest left":
      return "bg-[#eaf6f4] text-[#15776d]";
    case "Waiting on guest":
      return "bg-[#fdf2da] text-[#9a6700]";
    case "Updated":
      return "bg-[#eef3f6] text-[#5b6b7a]";
    case "Test clean":
      return "bg-[#f1eef6] text-[#6b5b8a]";
    case "Overdue":
      return "bg-[#fbeae8] text-[#b4453a]";
    case "In progress":
      return "border-[1.5px] border-[#24a89a] bg-white text-[#15776d]";
    case "Done":
      return "bg-[#e8eeea] text-[#5f7d6b]";
    case "Upcoming":
    default:
      return "bg-[#eef3f6] text-[#5b6b7a]";
  }
}

const SYNC_KEY = (propertyId: string) => `ical_last_sync:${propertyId}`;

export function readLastSyncAt(propertyId: string): number | null {
  try {
    const v = localStorage.getItem(SYNC_KEY(propertyId));
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function writeLastSyncAt(propertyId: string, at = Date.now()) {
  try {
    localStorage.setItem(SYNC_KEY(propertyId), String(at));
  } catch {
    /* ignore */
  }
}

export function formatLastSyncLabel(at: number | null, syncing?: boolean, justFailed?: boolean): string {
  if (syncing) return "Syncing…";
  if (justFailed) return "Couldn’t sync";
  if (!at) return "";
  const mins = Math.round((Date.now() - at) / 60000);
  if (mins < 2) return "Synced just now";
  if (mins < 60) return `Synced ${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `Synced ${hours}h ago`;
  return "Synced yesterday";
}
